import { Label, Select, SelectContent, SelectItem, SelectTrigger, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { usePatchOrganization } from '@/api/rest/resources/organization';
import { RoleEnum } from '@/api/rest/resources/types/membership';
import type { GetAvailableCurrenciesSuccessDto } from '@/api/rest/resources/types/organization';
import { useGetUserMe } from '@/api/rest/resources/user';
import { useCurrency } from '@/hooks/useCurrency';
import { useUserContext } from '@/lib/auth';
import { useGetAvailableCurrencies } from '@/pages/shared/hooks/useGetAvailableCurrencies';
import { useIsLimitedAccessUser } from '@/pages/shared/hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '@/pages/shared/hooks/useIsOrganizationMember';

type Currency = GetAvailableCurrenciesSuccessDto['currencies'][0];

export const SelectCurrency = () => {
  const { t } = useTranslation();
  const { data: user } = useGetUserMe();
  const { updateUserPartially } = useUserContext();
  const { currencyCode: currentCurrency } = useCurrency();
  const { data: currenciesData } = useGetAvailableCurrencies();
  const { mutate: updateOrganization } = usePatchOrganization();
  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();

  const isOrgAdmin = user?.membership?.role === RoleEnum.admin;
  if (!isOrgAdmin || !user || isLimitedAccessUser || !isOrganizationMember) {
    return null;
  }

  const currencies: Currency[] = currenciesData?.currencies || [];

  const changeCurrency = (currencyCode: string) => {
    if (currencyCode === currentCurrency) return;

    updateOrganization(
      {
        pathVariables: { id: user.membership.organization.id },
        bodyData: { currency_code: currencyCode },
      },
      {
        onSuccess: (updatedOrganization) => {
          updateUserPartially({
            membership: {
              ...user.membership,
              organization: {
                ...user.membership.organization,
                currency_code: updatedOrganization.currency_code,
              },
            },
          });
        },
      },
    );
  };

  if (currencies.length === 0) {
    return (
      <div className='w-full max-w-xs mt-1.5' data-testid='select-currency-empty'>
        {t('shared.account.labels.noCurrenciesAvailable')}
      </div>
    );
  }

  const selectedCurrency = currencies.find((curr: Currency) => curr.code === currentCurrency);

  return (
    <Stack spacing={2} className='max-w-xs py-2' data-testid='select-currency'>
      <Label className='typography-body2'>{t('shared.account.labels.currency')}</Label>
      <Select value={selectedCurrency?.code || ''} onValueChange={changeCurrency}>
        <SelectTrigger>
          {selectedCurrency
            ? `${selectedCurrency.code} - ${selectedCurrency.name}`
            : t('shared.account.labels.currency')}
        </SelectTrigger>
        <SelectContent>
          {currencies.map((currency: Currency) => (
            <SelectItem key={currency.code} value={currency.code}>
              {currency.code} - {currency.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Stack>
  );
};
